"use client";

import { useDeferredValue, useMemo, useState } from "react";
import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import { CrtOutcome } from "@/components/tools/number-theory/crt-results";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { formatBig } from "@/lib/tool-kit/math/bigint";
import {
  MAX_CONGRUENCES,
  MAX_DIGITS,
  modularInverse,
  readInteger,
} from "@/lib/tool-kit/number-theory/modular";

/** English strings. A translated content file overrides these through `ui`. */
export const MODULAR_INVERSE_CALCULATOR_STRINGS = {
  mode: "Calculate",
  modeInverse: "Modular inverse",
  modeCrt: "Chinese remainder theorem",
  results: "Results",
  copyResult: "Copy result",
  copied: "Copied!",
  absent: "—",
  a: "Number a",
  aHint: "Any whole number, negative or larger than the modulus included.",
  n: "Modulus n",
  nHint: "A whole number of at least 2.",
  inverse: "Inverse of a modulo n",
  noInverse: "No inverse",
  gcd: "gcd(a, n)",
  reduced: "a mod n",
  summaryInverse: "{a}⁻¹ ≡ {x} (mod {n}), because {a} × {x} ≡ 1 (mod {n}).",
  summaryNone: "{a} has no inverse modulo {n}: gcd({a}, {n}) = {g}.",
  tableHeading: "Extended Euclidean algorithm",
  tableIntro:
    "Start with the rows n and a. Each new row subtracts q times the row above from the row before it, in the r, s and t columns alike, until r reaches 0. Every row satisfies r = n·s + a·t.",
  colIndex: "i",
  colQuotient: "q",
  colRemainder: "r",
  colS: "s",
  colT: "t",
  bezout:
    "The last non-zero row gives n·s + a·t = gcd: {n} × {s} + {a} × {t} = {g}.",
  readInverse:
    "The gcd is 1, so t is the inverse of a: {t} ≡ {x} (mod {n}) once reduced into 0 … {max}.",
  check: "Check: {a} × {x} = {product} = {q} × {n} + 1.",
  reduceNote: "{a} reduces to {r} modulo {n}, and the working uses {r}.",
  noInverseExplain:
    "The gcd is {g}, not 1, so {a} and {n} share a factor and no x satisfies {a}x ≡ 1 (mod {n}). An inverse exists only when a and n are coprime.",
  enterBoth: "Enter a number a and a modulus n to find the inverse.",
  badA: "a must be a whole number, with no decimals or fractions.",
  badN: "The modulus must be a whole number of at least 2.",
  tooLong: "Use numbers of at most {max} digits.",
  system: "Congruences, one per line",
  systemHint:
    "Write each as remainder and modulus: 2 mod 3, x ≡ 2 (mod 3) or 2, 3. Up to {max} lines. The moduli need not be coprime.",
  solution: "Solution",
  smallest: "Smallest non-negative x",
  combinedModulus: "Repeats every",
  general: "All solutions: x = {r} + {m}k for any whole number k.",
  summaryCrt: "x ≡ {r} (mod {m})",
  noSolution:
    "No solution: {g} does not divide {d}, so x ≡ {a1} (mod {m1}) and x ≡ {a2} (mod {m2}) cannot both hold.",
  mergeHeading: "Combining the congruences",
  mergeIntro:
    "Merge them one at a time. For x ≡ a₁ (mod m₁) and x ≡ a₂ (mod m₂), write x = a₁ + m₁k and solve for k; the result is a congruence modulo lcm(m₁, m₂).",
  mergeHeader: "x ≡ {a1} (mod {m1}) and x ≡ {a2} (mod {m2})",
  mergeCheck:
    "gcd({m1}, {m2}) = {g} divides {d} = {a2} − {a1}, so a solution exists.",
  mergeSolve:
    "x = {a1} + {m1}k, so {m1r}k ≡ {dr} (mod {m2r}), which gives k ≡ {k}.",
  mergeResult: "Result: x ≡ {r} (mod {m})",
  classicHeading: "The textbook formula",
  classicIntro:
    "The moduli are pairwise coprime, so x = Σ rᵢ·Mᵢ·yᵢ (mod M), where M is the product of the moduli, Mᵢ = M ÷ mᵢ, and yᵢ is the inverse of Mᵢ modulo mᵢ.",
  colRemainderIn: "Remainder",
  colModulus: "Modulus",
  colPartial: "M ÷ modulus",
  colInverse: "Inverse of that",
  colTerm: "Product of the three",
  classicSum: "M = {product}. The sum is {sum}, and {sum} mod {product} = {x}.",
  notCoprime:
    "Some moduli share a factor, so the textbook formula does not apply; the merging method above handles that case.",
  enterSystem: "Enter at least one congruence to solve the system.",
  badLines: "These lines are not a remainder and a modulus: {lines}.",
  smallModulus: "Every modulus must be at least 2. Check line {lines}.",
  tooMany: "Enter at most {max} congruences.",
} as const;

type Strings = typeof MODULAR_INVERSE_CALCULATOR_STRINGS;
type Mode = "inverse" | "crt";

const MONO_LINES =
  "space-y-1 rounded-lg border border-border bg-muted/30 p-4 font-mono text-muted-foreground text-sm leading-relaxed";
const PROSE_SM = "text-muted-foreground text-sm";

/** A negative coefficient in parentheses, so "17 × −367" reads as "17 × (−367)". */
function bracketed(value: bigint): string {
  return value < BigInt(0) ? `(${formatBig(value)})` : String(value);
}

function InverseResults({
  a,
  n,
  strings,
}: {
  a: bigint;
  n: bigint;
  strings: Strings;
}) {
  const result = useMemo(() => modularInverse(a, n), [a, n]);
  const x = result.inverse;
  const aText = formatBig(a);
  const summary =
    x === null
      ? t(strings.summaryNone, {
          a: aText,
          n: String(n),
          g: String(result.gcd),
        })
      : t(strings.summaryInverse, { a: aText, x: String(x), n: String(n) });
  const product = x === null ? null : result.reduced * x;
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.inverse}
          value={x === null ? strings.noInverse : String(x)}
        />
        <StatCard label={strings.gcd} value={String(result.gcd)} />
        <StatCard label={strings.reduced} value={String(result.reduced)} />
      </StatGrid>
      <p className="text-foreground text-sm">{summary}</p>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResult}
        small
        text={summary}
      />
      {a === result.reduced ? null : (
        <Notice>
          {t(strings.reduceNote, {
            a: aText,
            r: String(result.reduced),
            n: String(n),
          })}
        </Notice>
      )}
      <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
      <p className={PROSE_SM}>{strings.tableIntro}</p>
      <DataTable
        head={[
          strings.colIndex,
          strings.colQuotient,
          strings.colRemainder,
          strings.colS,
          strings.colT,
        ]}
        rows={result.rows.map((row) => [
          row.index,
          row.quotient === null ? strings.absent : String(row.quotient),
          String(row.remainder),
          formatBig(row.s),
          formatBig(row.t),
        ])}
      />
      <div className={MONO_LINES}>
        <p>
          {t(strings.bezout, {
            n: String(n),
            s: bracketed(result.s),
            a: String(result.reduced),
            t: bracketed(result.t),
            g: String(result.gcd),
          })}
        </p>
      </div>
      {x !== null && product !== null ? (
        <>
          <p className={PROSE_SM}>
            {t(strings.readInverse, {
              t: formatBig(result.t),
              x: String(x),
              n: String(n),
              max: String(n - BigInt(1)),
            })}
          </p>
          <p className={PROSE_SM}>
            {t(strings.check, {
              a: String(result.reduced),
              x: String(x),
              product: String(product),
              q: String(product / n),
              n: String(n),
            })}
          </p>
        </>
      ) : (
        <Notice>
          {t(strings.noInverseExplain, {
            g: String(result.gcd),
            a: String(result.reduced),
            n: String(n),
          })}
        </Notice>
      )}
    </>
  );
}

function InverseOutcome({
  rawA,
  rawN,
  strings,
}: {
  rawA: string;
  rawN: string;
  strings: Strings;
}) {
  const a = readInteger(rawA);
  const n = readInteger(rawN);
  if (a.kind === "empty" || n.kind === "empty") {
    return <Notice>{strings.enterBoth}</Notice>;
  }
  if (a.kind === "too-long" || n.kind === "too-long") {
    return <ErrorNotice>{t(strings.tooLong, { max: MAX_DIGITS })}</ErrorNotice>;
  }
  if (a.kind !== "ok") {
    return <ErrorNotice>{strings.badA}</ErrorNotice>;
  }
  if (n.kind !== "ok" || n.value < BigInt(2)) {
    return <ErrorNotice>{strings.badN}</ErrorNotice>;
  }
  return <InverseResults a={a.value} n={n.value} strings={strings} />;
}

export default function ModularInverseCalculatorWidget() {
  const strings = useToolUi(MODULAR_INVERSE_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("inverse");
  const [rawA, setRawA] = useState("17");
  const [rawN, setRawN] = useState("3120");
  const [system, setSystem] = useState(
    "x ≡ 2 (mod 3)\nx ≡ 3 (mod 5)\nx ≡ 2 (mod 7)"
  );
  const deferredA = useDeferredValue(rawA);
  const deferredN = useDeferredValue(rawN);
  const deferredSystem = useDeferredValue(system);
  return (
    <>
      <PillGroup
        label={strings.mode}
        name="mic-mode"
        onChange={setMode}
        options={[
          { value: "inverse", label: strings.modeInverse },
          { value: "crt", label: strings.modeCrt },
        ]}
        value={mode}
      />
      {mode === "inverse" ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            hint={strings.aHint}
            id="mic-a"
            label={strings.a}
            mono
            numeric
            onChange={setRawA}
            value={rawA}
          />
          <TextField
            hint={strings.nHint}
            id="mic-n"
            label={strings.n}
            mono
            numeric
            onChange={setRawN}
            value={rawN}
          />
        </div>
      ) : (
        <TextAreaField
          hint={t(strings.systemHint, { max: fmt(MAX_CONGRUENCES) })}
          id="mic-system"
          label={strings.system}
          onChange={setSystem}
          rows={4}
          value={system}
        />
      )}
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {mode === "inverse" ? (
          <InverseOutcome rawA={deferredA} rawN={deferredN} strings={strings} />
        ) : (
          <CrtOutcome raw={deferredSystem} strings={strings} />
        )}
      </section>
    </>
  );
}
